/**
 * Voxel ray traversal (Amanatides & Woo).
 *
 * Walks the ray voxel by voxel in order, so the first solid block it meets is
 * genuinely the nearest one - no sampling along the ray, no missed corners,
 * and cost proportional to distance rather than to a step size.
 *
 * Used for aiming at blocks, and later for line of sight, projectiles and
 * light. Pure maths over a SolidTest, like the rest of core/physics.
 */

import type { SolidTest } from "./voxelCollision.js";

export interface RaycastHit {
  /** Integer coordinates of the block that was hit. */
  x: number;
  y: number;
  z: number;
  /**
   * Outward normal of the face entered, as -1/0/1 per axis. Adding this to
   * the hit position gives the empty cell in front of that face - which is
   * exactly where a newly placed block goes.
   */
  nx: number;
  ny: number;
  nz: number;
  /** Distance along the ray, in blocks. */
  distance: number;
}

/**
 * Cast a ray through the voxel grid.
 *
 * `dx, dy, dz` should be normalised; if it is not, `distance` and
 * `maxDistance` are measured in units of that vector's length instead.
 * Returns null if nothing solid is met within `maxDistance`.
 */
export function raycastVoxels(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxDistance: number,
  isSolid: SolidTest,
): RaycastHit | null {
  let x = Math.floor(ox);
  let y = Math.floor(oy);
  let z = Math.floor(oz);

  // Standing inside a block counts as an immediate hit; without this you can
  // aim at the block you are buried in and have the ray start past it.
  if (isSolid(x, y, z)) {
    return { x, y, z, nx: 0, ny: 0, nz: 0, distance: 0 };
  }

  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;

  // How far along the ray a full voxel of travel costs, per axis.
  const tDeltaX = stepX === 0 ? Infinity : Math.abs(1 / dx);
  const tDeltaY = stepY === 0 ? Infinity : Math.abs(1 / dy);
  const tDeltaZ = stepZ === 0 ? Infinity : Math.abs(1 / dz);

  // Distance to the first voxel boundary on each axis.
  let tMaxX = stepX === 0 ? Infinity : ((stepX > 0 ? x + 1 : x) - ox) / dx;
  let tMaxY = stepY === 0 ? Infinity : ((stepY > 0 ? y + 1 : y) - oy) / dy;
  let tMaxZ = stepZ === 0 ? Infinity : ((stepZ > 0 ? z + 1 : z) - oz) / dz;

  let nx = 0;
  let ny = 0;
  let nz = 0;
  let distance = 0;

  for (;;) {
    // Cross whichever boundary comes first.
    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      if (tMaxX > maxDistance) return null;
      x += stepX;
      // We entered through the face opposite the direction of travel.
      nx = -stepX; ny = 0; nz = 0;
      distance = tMaxX;
      tMaxX += tDeltaX;
    } else if (tMaxY < tMaxZ) {
      if (tMaxY > maxDistance) return null;
      y += stepY;
      nx = 0; ny = -stepY; nz = 0;
      distance = tMaxY;
      tMaxY += tDeltaY;
    } else {
      if (tMaxZ > maxDistance) return null;
      z += stepZ;
      nx = 0; ny = 0; nz = -stepZ;
      distance = tMaxZ;
      tMaxZ += tDeltaZ;
    }

    if (isSolid(x, y, z)) {
      return { x, y, z, nx, ny, nz, distance };
    }
  }
}

/**
 * Aim at nothing, and still put a block down - flush against something.
 *
 * "Гравець стоїть на блоці, попереду пустота, він може поставити блок
 * впритул до свого блока просто цілячись в пустоту перед блоком." On a map
 * that is one block and the sky there is nothing to click on but the block
 * under your feet, and building outwards meant crouching at its edge to
 * catch its side face. This walks the same ray through the empty cells it
 * passes and stops at the first one that touches something solid - and
 * answers as if the ray had hit *that* solid neighbour on the face nearest
 * the empty cell, so whoever asked can place exactly as they would from an
 * ordinary hit: `hit + normal` is the empty cell.
 *
 * **Only when the ray hits nothing.** A ray that meets a block is answered
 * by `raycastVoxels`; this is for when it does not, and callers ask it
 * second. Asking it first would put blocks in the air in front of every
 * wall instead of on the wall.
 *
 * `isFree` says whether a block may go in a cell at all - air, not inside
 * the player, not on top of somebody. A cell that is not free is walked
 * past, not refused: the void beside your feet is still there past the cell
 * you are standing in.
 *
 * Which neighbour, when the empty cell touches several: the one below it
 * first, because the thing you are extending is nearly always the thing you
 * are standing on; then the four sides; the one above last.
 */
export function raycastBeside(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxDistance: number,
  isSolid: SolidTest,
  isFree: SolidTest,
): RaycastHit | null {
  let x = Math.floor(ox);
  let y = Math.floor(oy);
  let z = Math.floor(oz);
  if (isSolid(x, y, z)) return null;

  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;
  const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;
  const tDeltaX = stepX === 0 ? Infinity : Math.abs(1 / dx);
  const tDeltaY = stepY === 0 ? Infinity : Math.abs(1 / dy);
  const tDeltaZ = stepZ === 0 ? Infinity : Math.abs(1 / dz);
  let tMaxX = stepX === 0 ? Infinity : ((stepX > 0 ? x + 1 : x) - ox) / dx;
  let tMaxY = stepY === 0 ? Infinity : ((stepY > 0 ? y + 1 : y) - oy) / dy;
  let tMaxZ = stepZ === 0 ? Infinity : ((stepZ > 0 ? z + 1 : z) - oz) / dz;
  let distance = 0;

  for (;;) {
    if (tMaxX < tMaxY && tMaxX < tMaxZ) {
      if (tMaxX > maxDistance) return null;
      x += stepX; distance = tMaxX; tMaxX += tDeltaX;
    } else if (tMaxY < tMaxZ) {
      if (tMaxY > maxDistance) return null;
      y += stepY; distance = tMaxY; tMaxY += tDeltaY;
    } else {
      if (tMaxZ > maxDistance) return null;
      z += stepZ; distance = tMaxZ; tMaxZ += tDeltaZ;
    }
    // Something solid in the way is the ordinary ray's business, not ours.
    if (isSolid(x, y, z)) return null;
    if (!isFree(x, y, z)) continue;
    const beside = solidBeside(x, y, z, isSolid);
    if (beside === null) continue;
    return {
      x: x + beside[0], y: y + beside[1], z: z + beside[2],
      nx: -beside[0], ny: -beside[1], nz: -beside[2],
      distance,
    };
  }
}

/** Below, the four sides, above - the first solid one, as an offset. */
function solidBeside(
  x: number, y: number, z: number, isSolid: SolidTest,
): readonly [number, number, number] | null {
  const around: readonly (readonly [number, number, number])[] = [
    [0, -1, 0], [-1, 0, 0], [1, 0, 0], [0, 0, -1], [0, 0, 1], [0, 1, 0],
  ];
  for (const [ax, ay, az] of around) {
    if (isSolid(x + ax, y + ay, z + az)) return [ax, ay, az];
  }
  return null;
}
